/**
 * Money rounding that agrees with the server.
 *
 * The server rounds every amount with `BigDecimal.setScale(n, RoundingMode.HALF_UP)`. The browser
 * copies of `Math.round(n * 100) / 100` scattered through this app do NOT agree with that, in two
 * independent ways:
 *
 * 1. **Binary float artefact.** A decimal like `1.005` is stored slightly *below* the true half
 *    (`1.005 * 100 === 100.49999999999999`), so `Math.round` rounds it down where the server rounds
 *    it up. `1.00` vs `1.01`.
 * 2. **`Math.round` is half-up toward +Infinity; `HALF_UP` is half-away-from-zero.** Every exactly
 *    representable negative half diverges by one minor unit: `Math.round(-12.5) === -12`, so
 *    `-0.125` becomes `-0.12` where the server says `-0.13`.
 *
 * Neither is hypothetical — both show up on credit notes, allocation remainders and FX deltas,
 * where a one-cent disagreement between what the browser previewed and what the server saved is the
 * difference between a send succeeding and a permanent conflict.
 *
 * `roundMoney` fixes both: it works on the magnitude (so halves round away from zero) and applies a
 * *relative* epsilon correction (so the artefact is corrected at any magnitude, not just near 1.0
 * where `Number.EPSILON` happens to be the right size).
 *
 * **This is a safety net, not a licence to compute money in the browser.** Where the server has
 * already produced an authoritative number — invoice totals, allocation remainders, resolver
 * columns — render the server's number and do not round at all.
 */

/**
 * Currencies whose minor unit is not 2 digits. Everything absent from this map is 2.
 * Matches the server's `Currency.getInstance(code).defaultFractionDigits` for the codes this
 * platform actually trades in.
 */
const MINOR_DIGITS: Readonly<Record<string, number>> = {
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
  JPY: 0,
  KRW: 0,
  VND: 0,
  CLP: 0,
  ISK: 0,
}

/** Minor-unit digits for an ISO currency code. Unknown or missing code → 2. */
export function minorDigitsFor(currency: string | null | undefined): number {
  if (!currency) return 2
  return MINOR_DIGITS[currency.trim().toUpperCase()] ?? 2
}

/**
 * Round to a currency's minor unit, matching Java's `BigDecimal.setScale(minorDigits, HALF_UP)`.
 *
 * Half values round away from zero (`0.125 → 0.13`, `-0.125 → -0.13`). Negative zero is
 * normalised to `0`, because the plan forbids displaying `-0.00`.
 *
 * Non-finite input is returned unchanged rather than silently becoming a number.
 */
export function roundMoney(value: number, minorDigits = 2): number {
  if (!Number.isFinite(value)) return value
  const factor = 10 ** minorDigits
  const scaled = Math.abs(value) * factor
  // Relative nudge: pulls a value that landed one ULP below its true half back onto the half, at
  // any magnitude. An absolute `+ Number.EPSILON` only works near 1.0.
  const corrected = scaled + scaled * Number.EPSILON
  const rounded = (Math.sign(value) * Math.round(corrected)) / factor
  return rounded === 0 ? 0 : rounded
}

/** Round to a currency's own minor unit. `roundInCurrency(1.005, 'KWD') === 1.005`. */
export function roundInCurrency(value: number, currency: string | null | undefined): number {
  return roundMoney(value, minorDigitsFor(currency))
}
